import { NodeAxios } from '../api'

export type FoundationPasteImportMode = 'fill_blanks' | 'replace_all'

export type FoundationPasteImportPreflightIssue = {
    code: string
    severity: 'info' | 'warning' | 'error'
    message: string
}

export type FoundationPasteImportPreflight = {
    pastedTargetCount: number
    existingTargetCount: number
    estimatedCredits: number
    mode: FoundationPasteImportMode
    issues: FoundationPasteImportPreflightIssue[]
    suggestReplaceAll: boolean
}

export type FoundationPasteImportProgress = {
    jobId: string
    status: 'queued' | 'running' | 'complete' | 'failed'
    step?: string
    label?: string
    detail?: string
    percent?: number
    error?: string
    summary?: {
        targets: number
        tfd: number
        alo: number
        claims: number
        briefs: number
        skipped: number
        warnings: number
    }
    warnings?: string[]
}

export const foundationPasteImportService = {
    async preflightImport(
        runId: string,
        params: {
            pastedContent: string
            additionalContext?: string
            mode?: FoundationPasteImportMode
        }
    ): Promise<{ preflight: FoundationPasteImportPreflight }> {
        return await NodeAxios.post(`/persuasion-lab/runs/${runId}/import-from-paste/preflight`, params)
    },

    async startImport(
        runId: string,
        params: {
            pastedContent: string
            additionalContext?: string
            mode?: FoundationPasteImportMode
            confirmReplace?: boolean
        }
    ): Promise<{ jobId: string; estimatedCredits: number; mode: FoundationPasteImportMode }> {
        // NodeAxios response interceptor already returns response.data
        return await NodeAxios.post(`/persuasion-lab/runs/${runId}/import-from-paste`, params)
    },

    async getImportStatus(runId: string, jobId: string): Promise<{ progress: FoundationPasteImportProgress }> {
        return await NodeAxios.get(`/persuasion-lab/runs/${runId}/import-from-paste/${jobId}`)
    },

    async getActiveImport(runId: string): Promise<{ progress: FoundationPasteImportProgress | null }> {
        return await NodeAxios.get(`/persuasion-lab/runs/${runId}/import-from-paste/active`)
    },

    async clearImportSession(runId: string): Promise<{ ok: boolean }> {
        return await NodeAxios.delete(`/persuasion-lab/runs/${runId}/import-from-paste/active`)
    },

    async recoverStuckImport(
        runId: string
    ): Promise<{ ok: boolean; briefId?: string; progress?: FoundationPasteImportProgress }> {
        return await NodeAxios.post(`/persuasion-lab/runs/${runId}/import-from-paste/recover`, {})
    },

    async listFoundationTargets(runId: string): Promise<{ targets: Array<Record<string, unknown>> }> {
        return await NodeAxios.get(`/persuasion-lab/runs/${runId}/foundation/targets`)
    },
}

export const PASTE_IMPORT_STEPS = [
    { id: 'inventory', label: 'Inventory' },
    { id: 'goals', label: 'Goals' },
    { id: 'targets', label: 'Audiences (deep research)' },
    { id: 'tfd', label: 'Mindset (TFD)' },
    { id: 'alo', label: 'Brand Truth (ALO)' },
    { id: 'symbols', label: 'Brand Voice' },
    { id: 'attached_entities', label: 'Attached entities' },
    { id: 'strategic_angles', label: 'Strategic angles' },
    { id: 'claims', label: 'Claims' },
    { id: 'campaign_angle', label: 'Campaign angle' },
    { id: 'value_props', label: 'Value props' },
    { id: 'briefs', label: 'Briefs' },
] as const

export function buildPasteTemplate(anchorType: 'company' | 'product' | 'person' = 'company'): string {
    const subjectLabel =
        anchorType === 'product' ? 'Product' : anchorType === 'person' ? 'Person / Expert' : 'Company'

    if (anchorType === 'company') {
        return `# Foundational Profile — Paste Template (Company)

## Subject & Goals
Company name:
Primary goals (near-term and long-term):

## Audience Segment Anchors (company shelf — WHO lenses, not buyer dramas)

### Segment 1 — Example segment label
Slug: example_segment
Axis: role_buyer
Segment kind: commercial
Description: One sentence — what this lens helps the system recognize.
Brand relevance: Why this lens matters across product lines.
Stable traits:
- Trait one
- Trait two
- Trait three
Decision tendencies:
- Tendency one
- Tendency two
- Tendency three
TFD hints:
- Think: What they should believe about the brand through this lens
- Feel: How they should feel
- Do: What they should do next

### Segment 2
Slug: second_segment
Axis: purchase_economics
Segment kind: commercial
Description:
Brand relevance:
Stable traits:
-
-
-
Decision tendencies:
-
-
-
TFD hints:
- Think:
- Feel:
- Do:

## Brand Truth (Assets / Liabilities / Omissions)
Assets:
Liabilities:
Omissions:

## Brand Voice / Symbols
Tone, metaphors, vocabulary to use or avoid:

## Strategic Angles (optional hints)
Angle 1:

## Claims (optional hints)
Claim ideas per angle:

## Value Props & Brief Intent
What should the primary message emphasize?
`
    }

    return `# Foundational Profile — Paste Template

## Subject & Goals
${subjectLabel} name:
Primary goals (near-term and long-term):

## Primary Audience
Name / title:
Role:
Description:
Pain points / objections:

## Communication Targets (list ALL buyer scenes — each gets full research)
### Target 1 (Primary)
Name: (scene-first — moment + tension)
Audience segment refs: slug-one, slug-two
Parent group: (Evaluation & Selection | Internal Champion & Adoption | Identity & Risk | …)
Scene:
Identity state:
Division:
Primary question:
Identification hook:
Recommended experiences: (actual product mechanisms)
Role:
Description:
TFD hints:
- Think:
- Feel:
- Do:

### Target 2
Name: (scene-first)
Audience segment refs:
Scene:
Division:
Identification hook:
Role:
Description:

## Brand Truth (Assets / Liabilities / Omissions)
Assets:
Liabilities:
Omissions:

## Brand Voice / Symbols
Tone, metaphors, vocabulary to use or avoid:

## Strategic Angles (optional hints)
Angle 1:
Angle 2:

## Claims (optional hints)
Claim ideas per angle:

## Value Props & Brief Intent
What should the primary message emphasize?
`
}
